const INPUT_SAMPLE_RATE = 24_000;

const int16ToFloat32 = (int16) => {
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i += 1) {
    float32[i] = int16[i] / 32768;
  }
  return float32;
};

const resampleLinear = (input, inputRate, outputRate) => {
  if (inputRate === outputRate) {
    return input;
  }

  const ratio = outputRate / inputRate;
  const outputLength = Math.max(1, Math.round(input.length * ratio));
  const output = new Float32Array(outputLength);

  for (let i = 0; i < outputLength; i += 1) {
    const index = i / ratio;
    const i0 = Math.floor(index);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = index - i0;
    output[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }

  return output;
};

export default class PcmAudioPlayer {
  constructor({ initialBufferMs = 200, minChunkMs = 60 } = {}) {
    this.initialBufferMs = initialBufferMs;
    this.minChunkMs = minChunkMs;

    this.audioContext = null;
    this.nextPlayTime = 0;

    this.pendingSamples = [];
    this.pendingByte = null;
    this.sources = new Set();

    this.destroyed = false;

    this.volume = 1.0;
    this.masterGainNode = null;
  }

  get isInitialized() {
    return Boolean(this.audioContext);
  }

  async initialize() {
    if (this.destroyed) {
      return;
    }

    if (this.audioContext) {
      if (this.audioContext.state === "suspended") {
        try {
          await this.audioContext.resume();
        } catch {
          // noop
        }
      }
      return;
    }

    const AudioContextImpl = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextImpl) {
      throw new Error("Web Audio API not supported");
    }

    this.audioContext = new AudioContextImpl();

    this.masterGainNode = this.audioContext.createGain();
    this.masterGainNode.gain.value = this.volume;
    this.masterGainNode.connect(this.audioContext.destination);

    try {
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }
    } catch {
      // Autoplay restrictions; keep text-only behavior.
    }

    this.nextPlayTime = this.audioContext.currentTime + this.initialBufferMs / 1000;
  }

  // Best-effort only. Web Audio output routing is not reliably supported across browsers.
  // Silently ignored if unsupported.
  setOutputDevice() {
    // noop
  }

  setVolume(vol) {
    this.volume = vol;
    if (this.masterGainNode) {
      this.masterGainNode.gain.value = vol; 
    }
  }

  pushPcmChunk(arrayBuffer) {
    if (this.destroyed || !this.audioContext) {
      return;
    }

    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      return;
    }

    let combined = new Uint8Array(arrayBuffer);

    if (this.pendingByte !== null) {
      const merged = new Uint8Array(combined.length + 1);
      merged[0] = this.pendingByte;
      merged.set(combined, 1);
      combined = merged;
      this.pendingByte = null;
    }

    if (combined.length % 2 !== 0) {
      this.pendingByte = combined[combined.length - 1];
      combined = combined.slice(0, -1);
    }

    if (combined.length === 0) {
      return;
    }

    // Realtime API proxy sends raw PCM bytes (16-bit signed int, little-endian).
    const int16 = new Int16Array(combined.buffer, combined.byteOffset, combined.byteLength / 2);
    const float32 = int16ToFloat32(int16);

    const outputRate = this.audioContext.sampleRate;
    const resampled = resampleLinear(float32, INPUT_SAMPLE_RATE, outputRate);

    for (let i = 0; i < resampled.length; i += 1) {
      this.pendingSamples.push(resampled[i]);
    }

    this.flushIfNeeded();
  }

  flushIfNeeded() {
    if (!this.audioContext) {
      return;
    }

    const minSamples = Math.floor(this.audioContext.sampleRate * (this.minChunkMs / 1000));
    if (this.pendingSamples.length < minSamples) {
      return;
    }

    // Slice out a chunk (up to ~2x min chunk) to reduce scheduling overhead.
    const chunkSize = Math.min(this.pendingSamples.length, minSamples * 2);
    const chunk = this.pendingSamples.splice(0, chunkSize);

    const buffer = this.audioContext.createBuffer(1, chunk.length, this.audioContext.sampleRate);
    buffer.copyToChannel(Float32Array.from(chunk), 0);

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;

    source.connect(this.masterGainNode);

    this.sources.add(source);
    source.onended = () => {
      this.sources.delete(source);
    };

    const now = this.audioContext.currentTime;
    const startTime = Math.max(this.nextPlayTime, now + 0.05);
    this.nextPlayTime = startTime + buffer.duration;

    try {
      source.start(startTime);
    } catch {
      this.sources.delete(source);
    }

    // Continue flushing if we still have enough samples.
    if (this.pendingSamples.length >= minSamples) {
      this.flushIfNeeded();
    }
  }

  flushAllPending() {
    if (!this.audioContext || this.pendingSamples.length === 0) return;

    const chunk = this.pendingSamples.splice(0, this.pendingSamples.length);

    const buffer = this.audioContext.createBuffer(
      1,
      chunk.length,
      this.audioContext.sampleRate
    );
    buffer.copyToChannel(Float32Array.from(chunk), 0);

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;

    source.connect(this.masterGainNode);

    this.sources.add(source);
    source.onended = () => {
      this.sources.delete(source);
    };

    const now = this.audioContext.currentTime;
    const startTime = Math.max(this.nextPlayTime, now + 0.05);
    this.nextPlayTime = startTime + buffer.duration;

    try {
      source.start(startTime);
    } catch {
      this.sources.delete(source);
    }
  }

  stop() {
    if (!this.audioContext) {
      this.pendingSamples = [];
      this.pendingByte = null;
      return;
    }

    this.pendingSamples = [];
    this.pendingByte = null;

    for (const source of this.sources) {
      try {
        source.onended = null;
        source.stop();
      } catch {
        // noop
      }
      try {
        source.disconnect();
      } catch {
        // noop
      }
    }

    this.sources.clear();
    this.nextPlayTime = this.audioContext.currentTime;
  }

  waitForDrain({ timeoutMs = 10_000 } = {}) {
    if (!this.audioContext) {
      return Promise.resolve();
    }

    const deadline = Date.now() + timeoutMs;

    // Ensure any remaining partial chunk is scheduled.
    this.flushAllPending();

    return new Promise((resolve) => {
      const check = () => {
        const now = Date.now();
        const drained =
          this.sources.size === 0 &&
          this.pendingSamples.length === 0 &&
          this.audioContext.currentTime >= this.nextPlayTime - 0.02;

        if (drained || now >= deadline) {
          resolve();
          return;
        }

        setTimeout(check, 50);
      };

      check();
    });
  }

  async destroy() {
    this.destroyed = true;
    this.stop();

    if (this.audioContext) {
      try {
        await this.audioContext.close();
      } catch {
        // noop
      }
      this.audioContext = null;
    }
  }
}
